/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { getPropertyIdLabel, isReservedOrgAlias, normalizeOrgAlias, ORG_SETTINGS_TRADE_CATEGORIES, ORG_SETTINGS_TRADE_DEFINITIONS } from '@kaiizen/kai-pro-api';
import {
  useAllOrgSettings,
  useCurrentOrg,
} from '@kaiizen/kai-pro-api-react-state';
import { useCallback, useMemo } from 'react';

import { useToast } from '@/Client/State/ToastContext';
import {
  SettingsHeaderRow,
  SettingsInsightBanner,
  SettingsIntro,
} from '@/Client/Widgets';

import { InputsOrganizationAliasPanel } from './InputsOrganizationAliasPanel';
import { InputsTradeGroupsPanel } from './InputsTradeGroupsPanel';
import { type SettingsRecord, useSettingsAutosave } from './useSettingsAutosave';

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

const MAX_TRADE_LABEL_LENGTH = 25;

type TradeSettingsTrade = {
  displayName?: string;
  description?: string;
  category?: string;
  isEnabled?: boolean;
};

type DraftTrade = {
  customLabel: string;
  description: string;
  isEnabled: boolean;
};

type DraftTradeRecord = Record<string, DraftTrade>;

/**
 * The page's two halves share one autosave record, and so one save chain.
 *
 * They persist to different props - `brandSettings.orgAlias` and `tradeSettings.trades` - but a
 * save replaces whole props, so two chains writing at once would race over the same stored
 * settings. One record keyed by trade slug plus the alias keeps every write ordered.
 */
const ORG_ALIAS_KEY = 'brandSettings.orgAlias';

type InputsDraftEntry =
  | { kind: 'trade'; trade: DraftTrade }
  | { kind: 'orgAlias'; alias: string };

type InputsDraftRecord = SettingsRecord<InputsDraftEntry>;

const getInputsDraftRecord = (
  orgAlias: string,
  draftTradeRecord: DraftTradeRecord,
): InputsDraftRecord => ({
  [ORG_ALIAS_KEY]: { kind: 'orgAlias', alias: orgAlias },
  ...Object.fromEntries(
    Object.entries(draftTradeRecord).map(([key, trade]) => [key, { kind: 'trade' as const, trade }]),
  ),
});

const getOrgAlias = (record: InputsDraftRecord) => {
  const entry = record[ORG_ALIAS_KEY];
  return entry?.kind === 'orgAlias' ? entry.alias : '';
};

const getTradeRecord = (record: InputsDraftRecord): DraftTradeRecord =>
  Object.fromEntries(
    Object.entries(record)
      .filter((entry): entry is [string, Extract<InputsDraftEntry, { kind: 'trade' }>] => entry[1].kind === 'trade')
      .map(([key, entry]) => [key, entry.trade]),
  );

const getOrgAliasRejection = (alias: string) => {
  const normalized = trimToUndefined(alias);
  return normalized && isReservedOrgAlias(normalized)
    ? 'The alias "KAI" is reserved for the product name.'
    : undefined;
};

const trimToUndefined = (value: string | undefined) => {
  const trimmedValue = value?.trim();
  return trimmedValue || undefined;
};

const getDraftTradeRecord = (
  configuredTrades: Record<string, TradeSettingsTrade> | undefined,
): DraftTradeRecord =>
  Object.fromEntries(
    ORG_SETTINGS_TRADE_DEFINITIONS.map(({ key, displayName }) => {
      const trade = configuredTrades?.[key];
      const effectiveDisplayName = trimToUndefined(trade?.displayName);

      return [
        key,
        {
          customLabel: effectiveDisplayName && effectiveDisplayName !== displayName
            ? effectiveDisplayName
            : '',
          description: trimToUndefined(trade?.description) ?? '',
          isEnabled: trade?.isEnabled !== false,
        },
      ];
    }),
  );

const getNormalizedTradeSettingsRecord = (
  draftTradeRecord: DraftTradeRecord,
): Record<string, TradeSettingsTrade> =>
  Object.fromEntries(
    ORG_SETTINGS_TRADE_DEFINITIONS.map(({ key }) => {
      const draftTrade = draftTradeRecord[key];
      const customLabel = trimToUndefined(draftTrade?.customLabel);
      const description = trimToUndefined(draftTrade?.description);

      return [
        key,
        {
          ...(customLabel ? { displayName: customLabel } : {}),
          ...(description ? { description } : {}),
          ...(draftTrade?.isEnabled === false ? { isEnabled: false } : {}),
        },
      ];
    }),
  );

/**
 * Org Settings → Inputs (KAIDEV-4345).
 *
 * Autosaves, as the rest of the surface now does: no save control, no cancel, and no navigation
 * guard - which this tab never registered, so an edit here used to be lost silently on
 * navigation. A rejected alias stays on screen to be corrected and is never sent.
 */
export const InputsTabContent = () => {
  const { allOrgSettings, allOrgSettingsMutators, isLoading } = useAllOrgSettings();
  const { currentOrg } = useCurrentOrg();
  const { showErrorToast } = useToast();

  const persistedOrgAlias = trimToUndefined(allOrgSettings?.brandSettings?.orgAlias) ?? '';
  const persisted = useMemo(
    () =>
      getInputsDraftRecord(
        persistedOrgAlias,
        getDraftTradeRecord(
          allOrgSettings?.tradeSettings?.trades as
            | Record<string, TradeSettingsTrade>
            | undefined,
        ),
      ),
    [allOrgSettings?.tradeSettings?.trades, persistedOrgAlias],
  );

  /* Rethrown after reporting: the hook reverts the field that failed, and without a word on the
     page that revert would read as the edit simply undoing itself. */
  const save = useCallback(async (record: InputsDraftRecord) => {
    try {
      await allOrgSettingsMutators.update({
        brandSettings: { orgAlias: getOrgAlias(record) },
        tradeSettings: { trades: getNormalizedTradeSettingsRecord(getTradeRecord(record)) },
      });
    } catch (error) {
      showErrorToast(error, 'save input settings');
      throw error;
    }
  }, [allOrgSettingsMutators, showErrorToast]);

  const validate = useCallback(
    (key: string, record: InputsDraftRecord) => key === ORG_ALIAS_KEY ? getOrgAliasRejection(getOrgAlias(record)) : undefined,
    [],
  );

  /* The stored form of what a save sends: the alias comes back trimmed and the trades come back
     through the same normalizer, so raw equality never holds and the hook's guard would pin
     itself (KAIDEV-4345 review round 3). */
  const toStoredForm = useCallback(
    (record: InputsDraftRecord): InputsDraftRecord =>
      getInputsDraftRecord(
        // The server's own rule, not an approximation of it: trim, then cap at the max length.
        normalizeOrgAlias(getOrgAlias(record)) ?? '',
        getDraftTradeRecord(getNormalizedTradeSettingsRecord(getTradeRecord(record))),
      ),
    [],
  );

  const { draft, errorByKey, updateEntry, commitEntry, focusEntry } = useSettingsAutosave<InputsDraftEntry>(
    persisted,
    { save, validate, toStoredForm },
  );

  const draftOrgAlias = getOrgAlias(draft);
  const draftTradeRecord = useMemo(() => getTradeRecord(draft), [draft]);

  /* Said as it is typed rather than only once a save is attempted, so the rule is visible before
     the pause runs out. `validate` applies the same rule to what actually gets sent. */
  const orgAliasValidationMessage = getOrgAliasRejection(draftOrgAlias) ?? errorByKey[ORG_ALIAS_KEY];
  const propertyIdLabelPreview = getPropertyIdLabel(
    currentOrg?.name,
    draftOrgAlias,
  );

  const tradeCount = ORG_SETTINGS_TRADE_DEFINITIONS.length;
  const enabledTradeCount = ORG_SETTINGS_TRADE_DEFINITIONS.filter(
    ({ key }) => draftTradeRecord[key]?.isEnabled,
  ).length;
  const disabledTradeCount = tradeCount - enabledTradeCount;

  const groupedTrades = useMemo(
    () =>
      ORG_SETTINGS_TRADE_CATEGORIES.map(category => {
        const categoryDefinitions = ORG_SETTINGS_TRADE_DEFINITIONS.filter(
          tradeDefinition => tradeDefinition.category === category,
        );

        return {
          key: category,
          title: category,
          enabled: categoryDefinitions.some(
            ({ key }) => draftTradeRecord[key]?.isEnabled,
          ),
          items: categoryDefinitions.map(({ key, displayName }) => ({
            slug: key,
            defaultLabel: displayName,
            customLabel: draftTradeRecord[key]?.customLabel ?? '',
            characterCountLabel: `${draftTradeRecord[key]?.customLabel.length ?? 0}/${MAX_TRADE_LABEL_LENGTH}`,
            enabled: draftTradeRecord[key]?.isEnabled ?? true,
          })),
        };
      }).filter(group => group.items.length > 0),
    [draftTradeRecord],
  );

  const updateTrade = (
    tradeSlug: string,
    updates: Partial<DraftTrade>,
    options?: { immediate?: boolean },
  ) => {
    const trade = draftTradeRecord[tradeSlug];
    if (!trade) return;
    /* Resolved against the stored draft rather than this render's copy: a group toggle changes
       several trades in one tick, and each has to build on what is actually there. */
    updateEntry(
      tradeSlug,
      previous => ({
        kind: 'trade',
        trade: { ...(previous?.kind === 'trade' ? previous.trade : trade), ...updates },
      }),
      options,
    );
  };

  // Text waits out the typing; a toggle is a decision already made, so it saves at once.
  const handleCustomLabelChange = (tradeSlug: string, value: string) => updateTrade(tradeSlug, { customLabel: value });

  const handleToggleTrade = (tradeSlug: string) =>
    updateTrade(
      tradeSlug,
      { isEnabled: !draftTradeRecord[tradeSlug]?.isEnabled },
      { immediate: true },
    );

  /* A group's toggle is several fields at once, so each saves in its own right. They queue behind
     one another on the single chain, and each payload is built when its turn comes - so the last
     one to land carries all of them rather than erasing the rest. */
  const handleToggleGroup = (groupKey: string) => {
    const groupTradeSlugs = ORG_SETTINGS_TRADE_DEFINITIONS
      .filter(tradeDefinition => tradeDefinition.category === groupKey)
      .map(tradeDefinition => tradeDefinition.key);
    const isEnabled = !groupTradeSlugs.some(tradeSlug => draftTradeRecord[tradeSlug]?.isEnabled);

    groupTradeSlugs.forEach(tradeSlug => updateTrade(tradeSlug, { isEnabled }, { immediate: true }));
  };

  return (
    <div css={CONTENT_STYLE}>
      <SettingsInsightBanner
        title='Input Settings Insight'
        body='Add an org alias to control the property ID label, then customize or disable trades without affecting existing projects.'
        dismissCookieKey='org-settings-inputs-insight-dismissed'
      />
      {
        /* No save control: every change here is already stored. */
      }
      <SettingsHeaderRow
        title='Input Settings'
        counts={{
          lead: `${tradeCount} trades`,
          details: [`${enabledTradeCount} enabled`, `${disabledTradeCount} disabled`],
        }}
        hideDefaultActions
      />
      <InputsOrganizationAliasPanel
        disabled={isLoading}
        orgAlias={draftOrgAlias}
        previewLabel={propertyIdLabelPreview}
        validationMessage={orgAliasValidationMessage}
        onOrgAliasChange={alias => updateEntry(ORG_ALIAS_KEY, { kind: 'orgAlias', alias })}
        onOrgAliasBlur={() => commitEntry(ORG_ALIAS_KEY)}
        onOrgAliasFocus={() => focusEntry(ORG_ALIAS_KEY)}
      />
      <SettingsIntro
        title='Trade Types'
        body='Trades are the types of work performed on a renovation. Add a custom label if you want a different name across the app. Disabled trades are hidden from new projects but won’t affect existing ones.'
        bodyTone='default'
      />
      <InputsTradeGroupsPanel
        groups={groupedTrades}
        disabled={isLoading}
        onCustomLabelChange={handleCustomLabelChange}
        onCustomLabelBlur={commitEntry}
        onCustomLabelFocus={focusEntry}
        onToggleClick={handleToggleTrade}
        onGroupToggleClick={handleToggleGroup}
      />
    </div>
  );
};
